import React, { useState, useEffect } from 'react';
import { ExternalLink, Clock, Heart, RefreshCw, Search, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Producer-focused filter categories - Compact names
const PRODUCER_FILTER_CATEGORIES = [
  { id: 'all', name: 'All News' },
  { id: 'daw', name: 'DAW', keywords: ['ableton', 'fl studio', 'logic pro', 'pro tools', 'DAW', 'cubase', 'studio one', 'bitwig', 'reaper'] },
  { id: 'plugins', name: 'Plugins', keywords: ['plugin', 'VST', 'AU', 'serum', 'massive', 'kontakt', 'omnisphere', 'fabfilter', 'izotope', 'waves', 'soundtoys', 'u-he', 'arturia'] },
  { id: 'hardware', name: 'Hardware', keywords: ['synthesizer', 'synth', 'hardware', 'analog', 'modular', 'eurorack', 'drum machine', 'midi', 'audio interface', 'roland', 'moog', 'sequential'] },
  { id: 'techniques', name: 'Techniques', keywords: ['mixing', 'mastering', 'sound design', 'arrangement', 'composition', 'tutorial', 'masterclass', 'tips', 'technique'] },
  { id: 'samples', name: 'Samples', keywords: ['sample', 'splice', 'loopmasters', 'sample pack', 'preset', 'sound library'] },
  { id: 'releases', name: 'Releases', keywords: ['release', 'label', 'sign', 'demo', 'remix', 'track', 'album', 'EP'] }
];

// All producer keywords for general filtering
const ALL_PRODUCER_KEYWORDS = [
  'producer', 'production', 'DAW', 'ableton', 'fl studio', 'logic pro', 'pro tools',
  'synthesizer', 'synth', 'plugin', 'VST', 'sample', 'sound design', 'mixing',
  'mastering', 'studio', 'beatmaker', 'drum machine', 'midi', 'audio interface',
  'kontakt', 'serum', 'massive', 'omnisphere', 'splice', 'loopmasters',
  'arrangement', 'composition', 'songwriting', 'tutorial', 'masterclass',
  'native instruments', 'roland', 'arturia', 'u-he', 'fab filter', 'izotope',
  'waves', 'soundtoys', 'analog', 'modular', 'eurorack', 'hardware',
  'release', 'track', 'remix', 'bootleg', 'edit', 'label', 'sign', 'demo'
];

const ProducerNewsTab = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await fetch(`${BACKEND_URL}/api/news/feed?force_refresh=${forceRefresh}`);
      
      if (response.ok) {
        const data = await response.json();
        const allNews = data.articles || data;
        
        // Filter news for producer-relevant content
        const producerNews = filterProducerNews(allNews);
        
        // Sort by date (newest first)
        const sortedNews = producerNews.sort((a, b) => 
          new Date(b.published || b.date) - new Date(a.published || a.date)
        );
        
        setNews(sortedNews);
      } else {
        throw new Error('Failed to load news');
      }
    } catch (error) {
      console.error('Error loading producer news:', error);
      setError('Unable to load news. Please try again later.');
      setNews(getMockNews());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const filterProducerNews = (articles) => {
    return articles.filter(article => {
      // Exclude Digital DJ Tips - reserved for Pro Tips section
      const source = (article.source || '').toLowerCase();
      if (source.includes('digital dj tips')) {
        return false;
      }
      
      const searchText = `${article.title || ''} ${article.summary || ''} ${article.description || ''}`.toLowerCase();
      return ALL_PRODUCER_KEYWORDS.some(keyword => searchText.includes(keyword.toLowerCase()));
    });
  };

  const getFilteredNews = () => {
    let filtered = news;
    
    // Apply category filter
    if (selectedCategory !== 'all') {
      const category = PRODUCER_FILTER_CATEGORIES.find(c => c.id === selectedCategory);
      if (category && category.keywords) {
        filtered = filtered.filter(article => {
          const searchText = `${article.title || ''} ${article.summary || ''} ${article.description || ''}`.toLowerCase();
          return category.keywords.some(keyword => searchText.includes(keyword.toLowerCase()));
        });
      }
    }
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(article => {
        const searchText = `${article.title || ''} ${article.summary || ''} ${article.description || ''}`.toLowerCase();
        return searchText.includes(term);
      });
    }
    
    return filtered.slice(0, 20); // Limit to 20 articles
  };

  const getMockNews = () => [
    {
      id: 1,
      title: "New Ableton Live 12.1 Features for Electronic Producers",
      published: new Date().toISOString(),
      summary: "Latest updates include enhanced MIDI effects, new synthesizers, and improved workflow features.",
      source: "Electronic Musician Magazine",
      url: "#",
      views: 1250,
      likes: 89
    },
    {
      id: 2,
      title: "Serum 2.0 Announced: Everything Producers Need to Know",
      published: new Date(Date.now() - 86400000).toISOString(),
      summary: "Xfer Records reveals Serum 2.0 with revolutionary new features.",
      source: "MusicRadar",
      url: "#",
      views: 2100,
      likes: 156
    },
    {
      id: 3,
      title: "ADE 2025 Conference: Producer Workshops Announced",
      published: new Date(Date.now() - 172800000).toISOString(),
      summary: "Amsterdam Dance Event returns with expanded producer workshops.",
      source: "DJ Mag",
      url: "#",
      views: 980,
      likes: 67
    }
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Recently';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffHours < 1) return 'Just now';
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const handleArticleClick = (article) => {
    if (article.url && article.url !== '#') {
      window.open(article.url, '_blank');
    }
  };

  const handleLike = async (e, articleId) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('beatseek_token');
      if (!token) {
        alert('Please login to like articles');
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/news/${articleId}/like`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setNews(prev => prev.map(article => 
          article.id === articleId 
            ? { ...article, likes: (article.likes || 0) + 1, user_liked: true }
            : article
        ));
      }
    } catch (error) {
      console.error('Error liking article:', error);
    }
  };

  const filteredNews = getFilteredNews();

  return (
    <div className="producer-news-tab">
      {/* Filter Bar */}
      <div style={{ 
        display: 'flex', 
        gap: '0.75rem', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Category Dropdown - Compact */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            background: 'rgba(30, 20, 50, 0.5)',
            border: '1px solid rgba(147, 51, 234, 0.3)',
            borderRadius: '10px',
            color: '#fff',
            fontSize: '0.875rem',
            cursor: 'pointer',
            minWidth: '100px',
            maxWidth: '120px'
          }}
        >
          {PRODUCER_FILTER_CATEGORIES.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>

        {/* Search Input - Takes more space */}
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <Search size={16} style={{ 
            position: 'absolute', 
            left: '1rem', 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: 'rgba(255,255,255,0.4)'
          }} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search news..."
            style={{
              width: '100%',
              padding: '0.75rem 1rem 0.75rem 2.5rem',
              background: 'rgba(30, 20, 50, 0.5)',
              border: '1px solid rgba(147, 51, 234, 0.3)',
              borderRadius: '10px',
              color: '#fff',
              fontSize: '0.875rem'
            }}
          />
        </div>

        {/* Refresh Button */}
        <button
          onClick={() => loadNews(true)}
          disabled={refreshing}
          style={{
            padding: '0.75rem 1rem',
            borderRadius: '10px',
            border: '1px solid rgba(147, 51, 234, 0.3)',
            background: 'rgba(30, 20, 50, 0.5)',
            color: refreshing ? 'rgba(255,255,255,0.4)' : '#c4b5fd',
            cursor: refreshing ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Filter Info */}
      <div style={{ 
        background: 'rgba(147, 51, 234, 0.1)', 
        border: '1px solid rgba(147, 51, 234, 0.2)',
        borderRadius: '8px',
        padding: '0.75rem 1rem',
        marginBottom: '1.5rem',
        fontSize: '0.8rem',
        color: 'rgba(255,255,255,0.6)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>🎛️ Showing {filteredNews.length} producer-focused articles</span>
        {selectedCategory !== 'all' && (
          <span style={{ color: '#fbbf24' }}>
            Filter: {PRODUCER_FILTER_CATEGORIES.find(c => c.id === selectedCategory)?.name}
          </span>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1rem',
          color: '#f87171',
          fontSize: '0.875rem'
        }}>
          {error}
        </div>
      )}

      {/* News List */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '32px', height: '32px', border: '3px solid rgba(147,51,234,0.3)', borderTopColor: '#9333ea', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
            <p style={{ color: 'rgba(255,255,255,0.5)' }}>Loading producer news...</p>
          </div>
        </div>
      ) : filteredNews.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {filteredNews.map((article) => (
            <div
              key={article.id}
              onClick={() => handleArticleClick(article)}
              style={{
                background: 'rgba(30, 20, 50, 0.5)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: '12px',
                padding: '1.25rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)';
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.2)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ color: '#fff', fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                    {article.title}
                  </h4>
                  <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem', marginBottom: '0.75rem', lineHeight: '1.5' }}>
                    {article.summary || article.description}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Clock size={12} /> {formatDate(article.published || article.date)}
                    </span>
                    <span style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#f87171', padding: '2px 8px', borderRadius: '4px' }}>
                      {article.source}
                    </span>
                    {article.views !== undefined && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        👁️ {article.views}
                      </span>
                    )}
                    <button
                      onClick={(e) => handleLike(e, article.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: article.user_liked ? 'rgba(239, 68, 68, 0.2)' : 'transparent',
                        border: 'none',
                        color: article.user_liked ? '#f87171' : 'rgba(255,255,255,0.4)',
                        cursor: 'pointer',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '0.75rem'
                      }}
                    >
                      <Heart size={12} fill={article.user_liked ? '#f87171' : 'none'} /> {article.likes || 0}
                    </button>
                  </div>
                </div>
                <ExternalLink size={18} style={{ color: '#f87171', flexShrink: 0, marginLeft: '1rem' }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '1rem' }}>
            {searchTerm || selectedCategory !== 'all' 
              ? 'No articles match your filters' 
              : 'No producer news available'}
          </p>
          {(searchTerm || selectedCategory !== 'all') && (
            <button
              onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                border: '1px solid #9333ea',
                background: 'transparent',
                color: '#c4b5fd',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ProducerNewsTab;
